import {
  Button,
  Chip,
  Content,
  Divider,
  Flex,
  FlyoutMenu,
  FlyoutMenuItem,
  FormInput,
  StrokeIcon,
  StrokeIconName,
  Tooltip,
  usePlatformInfo,
  usePlatformProps,
} from '@acsbe/ui';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Stripe from '../../../assets/stripe.svg';
import {
  useApplicationViewState,
  useBilling,
  useDomainSelection,
  useMerchantSession,
  VIEW_STATES,
} from '../../../context';
import { BillingMethod } from '../../../context/BillingContext';
import { useAnalytics } from '../../../services/analytics';
import { BUTTON_CLICKED } from '../../../services/analytics/event-names';
import { CMSFeedbackTriggerType } from '../../../type/UserFeedback';
import { useDateFormatter } from '../../../ui';
import { CMS } from '../../../utils/constants';
import { BorderedFlex } from './common';

interface BillingOption {
  value: string;
  label: string;
  default?: boolean;
  chip?: string;
}

interface BillingTaxInfo {
  billingId: string;
  taxToCollect: number;
  taxPercent: number;
  taxType: string | null;
}

const BILLING_OPTIONS: BillingOption[] = [
  {
    value: 'monthly',
    label: 'Monthly billing',
    default: true,
  },
  {
    value: 'yearly',
    label: 'Yearly billing',
    chip: 'Save 30%',
  },
];

interface BillingTemplateProps {
  onPaymentError: (
    msg: string | null,
    iconName?: StrokeIconName,
    iconScale?: 'danger' | 'warning' | 'success' | 'base',
  ) => void;
  clearPaymentError: () => void;
}

export const BillingTemplate = ({
  onPaymentError,
  clearPaymentError,
  ...props
}: BillingTemplateProps) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    licenseDetails,
    licenseId,
    refreshLicenseDetails,
    domain,
    currentAccessibeAccount,
  } = useDomainSelection();
  const { setApplicationViewState } = useApplicationViewState();
  const { portalMixpanelProps, merchantDetails, setUserFeedback } =
    useMerchantSession();
  const {
    billingMethods,
    selectedBillingId,
    isProcessingPayment,
    setIsProcessingPayment,
    selectedPlan,
    setSelectedPlan,
  } = useBilling();

  const p = usePlatformProps();
  const { uptoPhablet, uptoDesktopXs } = usePlatformInfo();
  const dateFormatter = useDateFormatter();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const [subtotal, setSubtotal] = useState<number | null>(null);
  const [tax, setTax] = useState<number | null>(null);
  const [taxType, setTaxType] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [proratedDate, setProratedDate] = useState<Date | null>(null);
  const [stripeProrationDate, setStripeProrationDate] = useState<number | null>(
    null,
  );

  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const billingMethodsArray: BillingMethod[] = Array.isArray(billingMethods)
    ? billingMethods
    : [];

  // Check if button should be enabled
  const hasBillingMethods = billingMethodsArray.length > 0;
  const hasPaymentDetails = subtotal !== null && total !== null;
  const hasSelectedBillingMethod =
    selectedBillingId && selectedBillingId.length > 0;
  const isButtonEnabled =
    hasBillingMethods &&
    hasPaymentDetails &&
    hasSelectedBillingMethod &&
    !isProcessingPayment;

  // Sync billing state with plan context
  const [billingCycle, setBillingCycle] = useState(
    selectedPlan!.monthly ? 'monthly' : 'yearly',
  );

  const isManualRenewal =
    licenseDetails?.autoRenew === false && licenseDetails?.giveGrace;

  const handleProratedDate = () => {
    if (
      licenseDetails?.datePlanExpires &&
      (licenseDetails?.status === 'active' ||
        licenseDetails?.status === 'cancelled')
    ) {
      setProratedDate(new Date(licenseDetails?.datePlanExpires));
      if (licenseDetails?.monthly && billingCycle === 'yearly') {
        const prorated = new Date();
        prorated.setFullYear(prorated.getFullYear() + 1);
        setProratedDate(prorated);
      }
    }
  };

  const handleConsentToggle = (checked: boolean) => {
    setIsTermsAccepted(checked);
    analytics.mixpanel.trackEvent(BUTTON_CLICKED, {
      buttonName: checked
        ? 'autoRenewConsentChecked'
        : 'autoRenewConsentUnchecked',
      buttonTag: 'autoRenewConsent',
    });
  };

  const features: string[] = [
    `Up to ${selectedPlan!.pricingModelPlan === 'visitsCount' ? selectedPlan!.visitsLimitation! / 1000 : selectedPlan!.limitation! / 1000}k websites ${selectedPlan!.pricingModelPlan === 'visitsCount' ? 'visits/month' : 'pages'}`,
    'Automated web accessibility',
    'Support: help center, email, live chat',
  ];

  const fetchPaymentDetails = useCallback(async () => {
    if (
      !licenseId ||
      !selectedPlan!.planId ||
      !currentAccessibeAccount?.stripeCustomerId
    ) {
      return;
    }

    setIsLoading(true);
    setTax(null);
    setTaxType(null);
    setTotal(null);
    try {
      const endpoint = licenseDetails?.active
        ? '/api/stripe/license-update-payment-details'
        : '/api/stripe/trial-and-expired-payment-details';

      const payload = {
        licenseId,
        monthly: selectedPlan!.monthly,
        planId: selectedPlan!.planId,
        discountEnabled: licenseDetails?.discountEnabled,
      };

      const response = await axios.post(endpoint, payload, {
        withCredentials: true,
      });

      const data = response.data.data;

      if (data.prorationDate) {
        setStripeProrationDate(data.prorationDate);
      }
      const currentBillingDetails = data.taxData.find(
        (billings: BillingTaxInfo) => billings.billingId === selectedBillingId,
      );
      setSubtotal(data.subTotal);
      if (currentBillingDetails) {
        setTotal(
          parseFloat(
            (data.total + currentBillingDetails.taxToCollect).toFixed(2),
          ),
        );
        setTax(parseFloat(currentBillingDetails.taxToCollect.toFixed(2)));
        setTaxType(currentBillingDetails.taxType);
      } else {
        setTotal(data.total);
        setTax(null);
        setTaxType(null);
      }
    } catch (error) {
      let errorMsg = 'Something Went Wrong';
      let iconName: StrokeIconName = 'ExclamationMarkWithCircle';
      let iconScale: 'danger' | 'warning' | 'success' | 'base' = 'danger';
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        errorMsg = error.response.data?.message || errorMsg;
        iconName = 'ExclamationMarkWithCircle';
        iconScale = 'danger';
      }
      onPaymentError && onPaymentError(errorMsg, iconName, iconScale);
      setSubtotal(null);
      setTax(null);
      setTotal(null);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    licenseId,
    selectedPlan,
    licenseDetails,
    selectedBillingId,
    currentAccessibeAccount?.stripeCustomerId,
  ]);

  const handleConfirmAndPay = async () => {
    if (!isButtonEnabled || !licenseId || !selectedPlan!.planId) {
      return;
    }

    setIsProcessingPayment(true);
    try {
      const payload = {
        licenseId,
        monthly: selectedPlan!.monthly,
        planId: selectedPlan!.planId,
        billingId: selectedBillingId,
        cmsMixpanelProps: JSON.stringify(portalMixpanelProps),
        ...(licenseDetails?.active ||
        (licenseDetails?.status === 'grace' && !isManualRenewal)
          ? { prorationDate: stripeProrationDate }
          : {}),
        discountEnabled: licenseDetails?.discountEnabled,
      };
      const isActiveLicense = licenseDetails?.active;
      const endpoint = isActiveLicense
        ? '/api/stripe/update-license-plan'
        : '/api/stripe/charge-and-activate-license';
      const response = await axios.post(endpoint, payload, {
        withCredentials: true,
      });

      clearPaymentError && clearPaymentError();
      // Refresh license details and go back to license page
      if (response.status === 200) {
        refreshLicenseDetails && refreshLicenseDetails();
        if (domain && merchantDetails?.source) {
          setUserFeedback({
            cmsFeedbackTriggerType: CMSFeedbackTriggerType.POST_PURCHASE,
            pluginInstallSource: merchantDetails.source as CMS,
            siteUrl: domain,
          });
        }
        setApplicationViewState(VIEW_STATES.DASHBOARD);
      }
    } catch (error) {
      let errorMsg = 'Payment failed. Please try again.';
      const iconName: StrokeIconName = 'Maintenance';
      const iconScale: 'danger' | 'warning' | 'success' | 'base' = 'warning';
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        errorMsg = error.response.data?.message || errorMsg;
      }
      onPaymentError && onPaymentError(errorMsg, iconName, iconScale);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  useEffect(() => {
    if (licenseId && selectedPlan!.planId) {
      fetchPaymentDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    licenseId,
    selectedPlan,
    selectedBillingId,
    currentAccessibeAccount?.stripeCustomerId,
  ]);

  // Sync billing state with plan context monthly value
  useEffect(() => {
    setBillingCycle(selectedPlan!.monthly ? 'monthly' : 'yearly');
    handleProratedDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan!.monthly]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleBillingChange = (value: string) => {
    setBillingCycle(value);
    setIsTermsAccepted(false);
    setSelectedPlan((prev) => ({
      ...prev!,
      monthly: value === 'monthly',
    }));
  };

  let confirmButton;
  if (!hasBillingMethods) {
    confirmButton = (
      <Tooltip
        content="Need to add payment method first"
        ignoreDisabled
        scale="invert"
      >
        <Button disabled fullWidth data-mixpanel="confirm-and-pay">
          <StrokeIcon name="Lock" />
          <span>Confirm & Pay</span>
        </Button>
      </Tooltip>
    );
  } else if (!hasPaymentDetails) {
    confirmButton = (
      <Tooltip
        content="Loading payment details..."
        ignoreDisabled
        scale="invert"
      >
        <Button disabled fullWidth>
          <StrokeIcon name="Lock" />
          <span>Confirm & Pay</span>
        </Button>
      </Tooltip>
    );
  } else if (!hasSelectedBillingMethod) {
    confirmButton = (
      <Tooltip
        content="Please select a payment method"
        ignoreDisabled
        scale="invert"
      >
        <Button disabled fullWidth>
          <StrokeIcon name="Lock" />
          <span>Confirm & Pay</span>
        </Button>
      </Tooltip>
    );
  } else {
    confirmButton = (
      <Button
        onClick={handleConfirmAndPay}
        disabled={isProcessingPayment || isLoading || !isTermsAccepted}
        scale="primary"
        variant="primary"
        fullWidth
      >
        <StrokeIcon name="Lock" />
        <span>{isProcessingPayment ? 'Processing...' : 'Confirm & Pay'}</span>
      </Button>
    );
  }

  return (
    <CardContainer
      scale="ghost"
      border="xxs"
      radius="xl"
      gap={0}
      {...p.fromDesktopSm({ maxWidth: '331px' })}
      {...p.fromDesktopLg({ maxWidth: '380px' })}
      {...p.uptoDesktopXs({ maxWidth: '100%', width: '100%' })}
    >
      {/* Section 1: Heading and Billing */}
      <BorderedFlex
        setBorderBottom
        align="flex-start"
        scale="accent"
        gap={1}
        self="stretch"
        paddingBlock={4}
        paddingInline={6}
      >
        <Content
          as="h2"
          size="md"
          emphasis="high"
          scale="base"
          lineHeight={1.25}
        >
          {selectedPlan!.planName} plan
        </Content>
        {/* Dropdown and chip trigger */}
        <Flex row align="center" gap={2}>
          <FlyoutMenu
            trigger={
              <ClickableRowFlex row align="center" gap={2} minHeight="24px">
                <Content
                  size="sm"
                  scale="base"
                  variant="element"
                  emphasis="low"
                  lineHeight={1}
                >
                  {billingCycle === 'monthly'
                    ? 'Billed monthly'
                    : 'Billed yearly'}
                </Content>
                <ChevronIcon name="ChevronDown" size="sm" />
                {billingCycle === 'yearly' && (
                  <Flex row align="flex-end" gap={1} paddingInline={1.5}>
                    <StyledChip scale="success" small noTextTransform>
                      {
                        BILLING_OPTIONS.find((opt) => opt.value === 'yearly')
                          ?.chip
                      }
                    </StyledChip>
                  </Flex>
                )}
              </ClickableRowFlex>
            }
            align="start"
          >
            {BILLING_OPTIONS.map((option: BillingOption) => (
              <FlyoutMenuItem
                compact
                key={option.value}
                data-mixpanel={option.label.toLowerCase().replace(' ', '-')}
                selected={billingCycle === option.value}
                onClick={() => handleBillingChange(option.value)}
              >
                <Flex row align="center" gap={2} self="stretch">
                  <Content
                    size="sm"
                    scale="base"
                    variant="static"
                    emphasis="none"
                    lineHeight={1}
                  >
                    {option.label}
                  </Content>
                  {option.chip && (
                    <Flex row align="flex-end" gap={1} paddingInline={1.5}>
                      <StyledChip scale="success" small noTextTransform>
                        {option.chip}
                      </StyledChip>
                    </Flex>
                  )}
                  {billingCycle === option.value && (
                    <StrokeIcon name="Check" size={16} />
                  )}
                </Flex>
              </FlyoutMenuItem>
            ))}
          </FlyoutMenu>
        </Flex>
      </BorderedFlex>
      {/* <Divider /> */}

      {/* Section 2: Plan Includes */}
      {!uptoDesktopXs && (
        <>
          <Flex
            align="flex-start"
            gap={1.5}
            self="stretch"
            paddingBlock={6}
            paddingInline={6}
          >
            <PlanIncludesLabel
              size="sm"
              scale="base"
              variant="static"
              emphasis="low"
              lineHeight={1.25}
            >
              Plan includes:
            </PlanIncludesLabel>
            <Flex gap={1} self="stretch">
              {features.map((feature: string, idx: number) => (
                <Flex key={idx} align="center" row self="stretch">
                  <StrokeIcon name="Check" size={16} scale="accent" />
                  <FeatureContent
                    size="sm"
                    scale="base"
                    variant="element"
                    emphasis="none"
                    lineHeight={1.5}
                  >
                    {feature}
                  </FeatureContent>
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Divider />
        </>
      )}

      {/* Section 3: Pricing, Button, Legal */}
      <Flex align="flex-start" gap={3} self="stretch" padding={6}>
        <Flex self="stretch" gap={2} align="flex-start">
          {subtotal !== null && total !== null && !uptoPhablet && (
            <Flex row justify="space-between" align="center" self="stretch">
              <Content
                size="sm"
                scale="accent"
                variant="static"
                emphasis="none"
                lineHeight={1.5}
              >
                Subtotal
              </Content>
              <Content
                size="sm"
                scale="accent"
                variant="static"
                emphasis="none"
                lineHeight={1.5}
              >
                {!isLoading ? (
                  proratedDate ||
                  (!licenseDetails?.active &&
                    licenseDetails?.discountEnabled &&
                    licenseDetails?.finalDiscount) ? (
                    <Flex row gap={1} align="center">
                      {proratedDate && (
                        <Tooltip
                          content={t('PRORATED.TOOLTIP_CONTENT')}
                          scale="invert"
                          zIndex={10}
                        >
                          <Content variant="element">
                            <StrokeIcon name="InfoMarkWithCircle" />
                          </Content>
                        </Tooltip>
                      )}
                      <s>${subtotal}</s>
                      <span>
                        ${parseFloat((tax ? total - tax : total).toFixed(2))}
                      </span>
                    </Flex>
                  ) : (
                    `$${subtotal}`
                  )
                ) : (
                  '-'
                )}
              </Content>
            </Flex>
          )}
          {subtotal !== null && proratedDate !== null && !uptoPhablet && (
            <Flex row justify="space-between" align="center" self="stretch">
              <Content
                size="sm"
                scale="accent"
                variant="static"
                emphasis="none"
                lineHeight={1.5}
              >
                {!isLoading
                  ? `Prorated cost until ${dateFormatter.format(proratedDate)}`
                  : '-'}
              </Content>
            </Flex>
          )}
          {taxType && !uptoPhablet && (
            <Flex row justify="space-between" align="center" self="stretch">
              <Content
                size="sm"
                scale="accent"
                variant="static"
                emphasis="none"
                lineHeight={1.5}
              >
                {taxType}
              </Content>
              <Content
                size="sm"
                scale="accent"
                variant="static"
                emphasis="none"
                lineHeight={1.5}
              >
                ${tax}
              </Content>
            </Flex>
          )}
          {(subtotal !== null || (tax !== null && tax > 0)) && !uptoPhablet && (
            <DottedDivider />
          )}
          {total !== null && (
            <Flex
              row
              justify="space-between"
              align="center"
              marginBlockStart={1}
              self="stretch"
            >
              <Content
                size="md"
                scale="base"
                variant="static"
                emphasis="low"
                lineHeight={1.25}
              >
                Total
              </Content>
              <Content
                size="xl"
                scale="base"
                variant="static"
                emphasis="low"
                lineHeight={1.25}
              >
                ${total}
                {billingCycle === 'monthly' ? '/mo' : '/yr'}
              </Content>
            </Flex>
          )}
        </Flex>
        <Flex gap={3} align="center" self="stretch">
          {confirmButton}
          <StyledContent
            align="center"
            scale="disabled"
            emphasis="none"
            lineHeight={1.25}
          >
            Powered by <StripeLogo />
          </StyledContent>
        </Flex>
        {!isManualRenewal && (
          <FormInput
            aria-describedby="auto-renew-consent"
            checked={isTermsAccepted}
            alignItems="flex-start"
            label={
              <Content
                emphasis="none"
                id="auto-renew-text"
                size="smb"
                scale="disabled"
                lineHeight={1.5}
              >
                {t(
                  `BILLING_PAGE.${billingCycle === 'monthly' ? 'AUTO_RENEW_MONTHLY' : 'AUTO_RENEW_ANNUAL'}`,
                )}
                <StyledSpan>*</StyledSpan>
              </Content>
            }
            layout="horizontal"
            onChange={(e) => handleConsentToggle(e.target.checked)}
            type="checkbox"
          />
        )}
      </Flex>
    </CardContainer>
  );
};

const CardContainer = styled(Flex)`
  box-shadow:
    0 4px 24px rgba(16, 30, 54, 0.12),
    0 2px 8px rgba(16, 30, 54, 0.08);
`;

const DottedDivider = styled.div`
  border-bottom: 1px dashed #e3e8f0;
  margin: 8px 0;
  width: 100%;
`;

const ClickableRowFlex = styled(Flex).attrs({
  as: 'button',
  type: 'button',
})`
  cursor: pointer;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
`;

const ChevronIcon = styled(StrokeIcon)`
  vertical-align: middle;
`;

const FeatureContent = styled(Content)`
  margin-left: 8px;
  white-space: pre-line;
`;

const PlanIncludesLabel = styled(Content)`
  margin-bottom: 4px;
`;

const StyledChip = styled(Chip)`
  display: flex;
  padding: 4px 6px;
  align-items: flex-end;
  gap: 2px;
  border-radius: 6px;
`;

const StripeLogo = styled(Stripe)`
  vertical-align: middle;
`;

const StyledContent = styled(Content)`
  font-size: 15px;
`;

const StyledSpan = styled.span`
  color: red;
`;
